import { Link } from "react-router-dom";
import { Receipt, ArrowSquareOut } from "@phosphor-icons/react";

const fmt$ = (n) => "$" + Number(n || 0).toLocaleString();

/**
 * Invoices tab — engagement-scoped invoice roster with totals footer.
 * Behavior-preserving extraction from EngagementDetail.
 */
export default function EngagementInvoicesTab({ invoices, onOpenInvoice }) {
  if (invoices.length === 0) {
    return (
      <div className="border border-dashed border-border p-10 text-center">
        <Receipt size={28} className="mx-auto text-muted-foreground mb-3" />
        <div className="text-sm text-muted-foreground max-w-sm mx-auto">
          No invoices yet for this engagement.
          <span className="block mt-2">
            Generate one from the{" "}
            <Link to="/invoices" className="text-primary hover:underline">Invoices workspace</Link>
            {" "}— it will thread the billed-to-date meter on this page automatically.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[1.4fr_1.2fr_0.7fr_0.9fr_60px] gap-4 px-5 py-3 border-b border-border text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
        <div>Invoice</div>
        <div>Period</div>
        <div>Status</div>
        <div className="text-right">Total</div>
        <div></div>
      </div>
      {invoices.map((inv) => {
        const tone = inv.status === "paid" ? "text-emerald-500"
          : inv.status === "sent" ? "text-primary"
          : inv.status === "overdue" ? "text-destructive"
          : "text-muted-foreground";
        return (
          <div
            key={inv.id}
            onClick={() => onOpenInvoice(inv)}
            className="grid grid-cols-[1.4fr_1.2fr_0.7fr_0.9fr_60px] gap-4 px-5 py-3 border-b border-border/60 last:border-b-0 items-center hover:bg-secondary/40 transition-colors cursor-pointer"
            data-testid={`eng-invoice-row-${inv.id}`}
          >
            <div>
              <div className="text-sm font-medium">{inv.invoice_number}</div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                {inv.issue_date ? `issued ${inv.issue_date}` : "no issue date"}
              </div>
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              {(inv.period_start || "—")} → {(inv.period_end || "—")}
            </div>
            <div className={`text-[0.625rem] font-mono uppercase tracking-widest ${tone}`}>{inv.status || "draft"}</div>
            <div className="text-right font-medium tabular-nums">{fmt$(inv.total || 0)}</div>
            <div className="text-right" onClick={(ev) => ev.stopPropagation()}>
              <Link to={`/invoices?open=${inv.id}`} className="text-primary hover:opacity-80 inline-flex items-center" title="Open in invoices workspace">
                <ArrowSquareOut size={14} />
              </Link>
            </div>
          </div>
        );
      })}
      <div className="grid grid-cols-[1.4fr_1.2fr_0.7fr_0.9fr_60px] gap-4 px-5 py-3 bg-secondary/30 text-[0.625rem] font-mono uppercase tracking-widest">
        <div className="col-span-3 text-muted-foreground">
          {invoices.filter((i) => i.status === "paid").length} paid ·{" "}
          {invoices.filter((i) => i.status === "sent").length} sent ·{" "}
          {invoices.filter((i) => (i.status || "draft") === "draft").length} draft
        </div>
        <div className="text-right text-foreground font-medium">
          {fmt$(invoices.reduce((s, i) => s + (i.total || 0), 0))}
        </div>
        <div></div>
      </div>
    </div>
  );
}
